import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiError, api, type Server } from '../components/api'
import {
  Alert,
  Button,
  Card,
  FormField,
  Input,
  Textarea,
} from '../components/ui'

type FormState = {
  kind: string
  plan: string
  contract_expires_at: string
  admin_url: string
  wp_info: string
  notes: string
  // 暗号化フィールド：reveal 成功時にだけ平文を保持
  ftp_info: string
  db_info: string
  // 「変更を送信するか」を管理（reveal 後に編集したら true）
  ftp_dirty: boolean
  db_dirty: boolean
}

const empty: FormState = {
  kind: 'xserver',
  plan: '',
  contract_expires_at: '',
  admin_url: '',
  wp_info: '',
  notes: '',
  ftp_info: '',
  db_info: '',
  ftp_dirty: false,
  db_dirty: false,
}

export function ServerEditPage() {
  const { id } = useParams()
  const serverId = id ? Number(id) : null
  const navigate = useNavigate()

  const [form, setForm] = useState<FormState>(empty)
  const [customerId, setCustomerId] = useState<number | null>(null)
  const [hasEncryptedFtp, setHasEncryptedFtp] = useState(false)
  const [hasEncryptedDb, setHasEncryptedDb] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const [revealing, setRevealing] = useState(false)
  const [revealError, setRevealError] = useState<string | null>(null)

  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!serverId) return
    let cancelled = false
    setLoading(true)
    api<Server>(`/api/servers/${serverId}`)
      .then((s) => {
        if (cancelled) return
        setCustomerId(s.customer_id)
        setHasEncryptedFtp(Boolean(s.ftp_info_encrypted))
        setHasEncryptedDb(Boolean(s.db_info_encrypted))
        setForm({
          kind: s.kind,
          plan: s.plan ?? '',
          contract_expires_at: s.contract_expires_at?.slice(0, 10) ?? '',
          admin_url: s.admin_url ?? '',
          wp_info: s.wp_info ?? '',
          notes: s.notes ?? '',
          ftp_info: '',
          db_info: '',
          ftp_dirty: false,
          db_dirty: false,
        })
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [serverId])

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setFieldErrors((prev) => {
      if (!prev[key]) return prev
      const { [key]: _, ...rest } = prev
      return rest
    })
  }

  const validate = () => {
    const errors: Record<string, string> = {}
    if (!form.kind.trim()) errors.kind = '種別は必須です'
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const requestReveal = async () => {
    if (!serverId) return
    setRevealError(null)
    setRevealing(true)
    try {
      const s = await api<Server>(`/api/servers/${serverId}`, {
        query: { reveal: 1 },
      })
      setForm((prev) => ({
        ...prev,
        ftp_info: s.ftp_info ?? '',
        db_info: s.db_info ?? '',
        ftp_dirty: false,
        db_dirty: false,
      }))
      setRevealed(true)
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setRevealError(
          'この顧客の FTP/DB 情報を復号する権限がありません。担当者または管理者に依頼してください。',
        )
      } else {
        setRevealError(
          e instanceof Error ? e.message : '復号に失敗しました',
        )
      }
    } finally {
      setRevealing(false)
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!serverId) return
    setError(null)
    if (!validate()) return
    setSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        kind: form.kind.trim(),
        plan: form.plan.trim() || null,
        contract_expires_at: form.contract_expires_at || null,
        admin_url: form.admin_url.trim() || null,
        wp_info: form.wp_info.trim() || null,
        notes: form.notes.trim() || null,
      }
      // ftp_info / db_info は reveal 後かつ変更時のみ送信
      // （未送信なら BE 側で据え置き）
      if (revealed && form.ftp_dirty) {
        body.ftp_info = form.ftp_info
      }
      if (revealed && form.db_dirty) {
        body.db_info = form.db_info
      }
      await api(`/api/servers/${serverId}`, { method: 'PUT', body })
      if (customerId) navigate(`/customers/${customerId}`)
      else navigate(-1)
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存に失敗しました')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <p className="muted">読み込み中…</p>

  const secretsAvailable = hasEncryptedFtp || hasEncryptedDb

  return (
    <>
      <div className="page-header">
        <h1>サーバーを編集</h1>
        {customerId && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/customers/${customerId}`)}
          >
            顧客詳細へ戻る
          </Button>
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <form onSubmit={handleSubmit} noValidate>
        <Card title="基本情報">
          <div className="form-row">
            <FormField
              label="種別"
              htmlFor="kind"
              required
              error={fieldErrors.kind}
              hint="xserver / conoha / sakura / aws / other"
            >
              <Input
                id="kind"
                value={form.kind}
                onChange={(e) => update('kind', e.target.value)}
                required
              />
            </FormField>
            <FormField label="プラン" htmlFor="plan">
              <Input
                id="plan"
                value={form.plan}
                onChange={(e) => update('plan', e.target.value)}
                placeholder="X10 / WING など"
              />
            </FormField>
            <FormField label="契約期限" htmlFor="contract_expires_at">
              <Input
                id="contract_expires_at"
                type="date"
                value={form.contract_expires_at}
                onChange={(e) => update('contract_expires_at', e.target.value)}
              />
            </FormField>
            <FormField label="管理画面 URL" htmlFor="admin_url">
              <Input
                id="admin_url"
                type="url"
                value={form.admin_url}
                onChange={(e) => update('admin_url', e.target.value)}
                className="mono"
                placeholder="https://..."
              />
            </FormField>
          </div>
        </Card>

        <Card title="FTP／DB 情報（AES-GCM 暗号化）">
          {secretsAvailable && !revealed && (
            <div
              className="row"
              style={{ justifyContent: 'space-between', marginBottom: 12 }}
            >
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                登録済みの暗号化値があります。復号して編集するには「復号」ボタンを押してください。
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={requestReveal}
                disabled={revealing}
              >
                {revealing ? '復号中…' : '復号して編集'}
              </Button>
            </div>
          )}
          {!secretsAvailable && !revealed && (
            <Alert variant="info">
              FTP/DB 情報は未登録です。下のフィールドに入力すると保存時に暗号化されます。
              入力する場合は「復号して編集」を押す必要はありません。
            </Alert>
          )}
          {revealError && <Alert variant="danger">{revealError}</Alert>}
          {revealed && (
            <Alert variant="success">
              復号しました。値を変更した場合のみ更新されます（未編集の項目は据え置き）。
            </Alert>
          )}

          <FormField
            label="FTP 情報"
            htmlFor="ftp_info"
            hint="例：host=sv1234.xserver.jp user=xxx pass=xxx port=21"
          >
            <Textarea
              id="ftp_info"
              value={form.ftp_info}
              disabled={secretsAvailable && !revealed}
              onChange={(e) => {
                update('ftp_info', e.target.value)
                update('ftp_dirty', true)
              }}
              className="mono"
              rows={3}
              placeholder={
                secretsAvailable && !revealed
                  ? '••••••••'
                  : '平文で入力（保存時に暗号化）'
              }
            />
          </FormField>
          <FormField
            label="DB 情報"
            htmlFor="db_info"
            hint="例：host=mysql1234.xserver.jp user=xxx pass=xxx db=xxx"
          >
            <Textarea
              id="db_info"
              value={form.db_info}
              disabled={secretsAvailable && !revealed}
              onChange={(e) => {
                update('db_info', e.target.value)
                update('db_dirty', true)
              }}
              className="mono"
              rows={3}
              placeholder={
                secretsAvailable && !revealed
                  ? '••••••••'
                  : '平文で入力（保存時に暗号化）'
              }
            />
          </FormField>
        </Card>

        <Card title="WP／その他">
          <FormField
            label="WP 情報（平文）"
            htmlFor="wp_info"
            hint="WP管理URL / バージョン / テーマ / 自動更新ON/OFF など"
          >
            <Textarea
              id="wp_info"
              value={form.wp_info}
              onChange={(e) => update('wp_info', e.target.value)}
              rows={3}
            />
          </FormField>
          <FormField label="メモ" htmlFor="notes">
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
              rows={3}
            />
          </FormField>
        </Card>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
            キャンセル
          </Button>
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? '保存中…' : '保存'}
          </Button>
        </div>
      </form>
    </>
  )
}
